import React, { useRef } from "react";
import {
  IconX,
  IconChevronDown,
  IconRotate2,
  IconLoader2,
  IconPrinter,
} from "@tabler/icons-react";
import { usePicklistByIdQuery } from "../../../apis/picklistApis";
import formatDate from "../../../utilities/helper";
import { useLanguage } from "../../../context/LanguageContext";

interface PrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  picklistId: string | null;
}

const PrintModal: React.FC<PrintModalProps> = ({
  isOpen,
  onClose,
  picklistId,
}) => {
  const { data: picklist, isLoading } = usePicklistByIdQuery(picklistId || "");
  const printRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();
  console.log("picklist", picklist);
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Picklist - ${picklist?.picklistNumber || ""}</title>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
          <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128&display=swap" rel="stylesheet">
          <style>
            @media print {
              @page { margin: 10mm; }
              body { -webkit-print-color-adjust: exact; }
              .no-print { display: none; }
            }
            .barcode {
              font-family: 'Libre Barcode 128', cursive;
              font-size: 40px;
              line-height: 1;
            }
            .barcode-sm {
              font-family: 'Libre Barcode 128', cursive;
              font-size: 32px;
              line-height: 1;
            }
          </style>
        </head>
        <body class="bg-white p-8">
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  if (!isOpen) return null;

  // Flatten items for printing
  const printItems =
    picklist?.picklistOrders?.flatMap((po: any) => {
      if (po.orderAllocations?.length > 0) {
        return po.orderAllocations.map((alloc: any) => ({
          sku: alloc.product?.sku || "",
          name: alloc.product?.itemName || "",
          qty: alloc.quantity || 0,
          location:
            alloc.whBinLocation?.binNumber ||
            alloc.whPalletLocation?.palletNumber ||
            "General",
          locationBarcode:
            alloc.whBinLocation?.barcodeLabel ||
            alloc.whPalletLocation?.barcodeLabel ||
            "",
          awb: po.order?.awbNumber || "",
        }));
      }
      return (
        po.order?.oLineItems?.map((item: any) => ({
          sku: item.sku || "",
          name: item.itemName || "",
          qty: item.quantity || 0,
          location: "",
          awb: po.order?.awbNumber || "",
        })) || []
      );
    }) || [];

  return (
    <>
      {/* <div className="fixed inset-0 z-[200] flex items-center justify-end bg-black/40 backdrop-blur-sm pr-4">
        <link
          href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128&display=swap"
          rel="stylesheet"
        />
        <style>{`
        .barcode {
          font-family: 'Libre Barcode 128', cursive;
          font-size: 40px;
          line-height: 1;
        }
        .barcode-sm {
          font-family: 'Libre Barcode 128', cursive;
          font-size: 32px;
          line-height: 1;
        }
      `}</style>
        <div className="w-full max-w-[550px] h-[90vh] relative animate-in slide-in-from-right duration-300">  
          <div className="flex items-center">
            <div className="bg-[#FDF8E5] flex items-center justify-between px-10 py-5 w-full rounded-tl-[40px] rounded-tr-[80px] shadow-sm">
              <h3 className="text-2xl font-black text-[#212121]">
                Print ({picklist?.picklistNumber || "..."})
              </h3>
            </div>
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={38}
                height={37}
                viewBox="0 0 38 37"
                fill="none"
                className="absolute -bottom-[1px] -left-[30px] rotate-[-2deg]"
              >
                <path
                  d="M0.5 0L4.86189 10.5496C10.4737 24.1222 22.9306 33.645 37.5 35.5L4.91509 37L0.5 0Z"
                  fill="#FCFDF8"
                />
              </svg>
              <button
                onClick={onClose}
                className="bg-[#F2F2F7] hover:bg-gray-200 rounded-full p-1 cursor-pointer w-12 h-12 ml-4 flex items-center justify-center flex-shrink-0 shadow-sm border border-white/50 transition-all"
              >
                <IconX size={20} className="text-[#212121]" />
              </button>
            </div>
          </div>
 
          <div className="bg-[#FCFDF8] rounded-b-[40px] rounded-tr-[40px] -mt-1 h-[calc(90vh-64px)] flex flex-col shadow-2xl border-t border-white/20">
            <div className="px-10 pt-8 pb-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-500">
                  Select Print Type
                </label>
                <div className="relative">
                  <select className="w-full bg-white border border-gray-100 rounded-2xl px-6 py-4 text-xs font-bold text-gray-400 appearance-none focus:outline-none shadow-sm focus:ring-2 focus:ring-[#FCB910]/20 transition-all">
                    <option>
                      Print Pickup List / Print List Seller / Print Pickup List
                      A4 / Print AWB 4*6 / Print AWB A4
                    </option>
                  </select>
                  <IconChevronDown
                    size={18}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>
            </div>
 
            <div className="px-10 py-2 flex-1 overflow-auto hide-scrollbar">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
                  <IconLoader2
                    size={48}
                    className="animate-spin text-[#FCB910] opacity-50"
                  />
                  <span className="font-bold text-sm tracking-widest uppercase">
                    Fetching Details
                  </span>
                </div>
              ) : picklist ? (
                <div className="bg-white rounded-3xl p-8 shadow-inner min-h-full border border-gray-50 relative group">
                  <div
                    ref={printRef}
                    className="w-full max-w-[800px] mx-auto bg-white text-black font-sans border-2 border-black"
                  > 
                    <div className="grid grid-cols-[1fr_2fr] border-b-2 border-black">
                      <div className="p-4 border-r-2 border-black flex items-center justify-center">
                        <div className="w-12 h-12 border border-gray-300 flex items-center justify-center text-gray-300">
                          <IconX size={20} />
                        </div>
                      </div>
                      <div className="p-4 relative">
                        <div className="absolute top-2 right-4 text-right">
                          <div className="w-16 h-16 border-2 border-black p-1"> 
                            <div className="grid grid-cols-4 gap-0.5 h-full opacity-80">
                              {Array.from({ length: 16 }).map((_, i) => (
                                <div
                                  key={i}
                                  className={`bg-black ${Math.random() > 0.5 ? "opacity-100" : "opacity-0"}`}
                                ></div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="mt-8 text-[10px] font-bold">
                          Order Date: {formatDate(picklist.createdAt)}
                        </div>
                      </div>
                    </div> 
                    <div className="grid grid-cols-2 border-b-2 border-black text-[10px]">
                      <div className="border-r-2 border-black">
                        <div className="bg-white border-b-2 border-black px-2 py-0.5 font-black uppercase">
                          Sender Info
                        </div>
                        <div className="p-2 leading-tight">
                          <p>
                            <span className="font-bold">Name:</span>{" "}
                            {picklist.client?.name || "Warehouse Manager"}
                          </p>
                          <p>
                            <span className="font-bold">Contact:</span>{" "}
                            {picklist.client?.email || ""}
                          </p>
                          <p>
                            <span className="font-bold">Address:</span> Main Hub
                          </p>
                          <p>
                            <span className="font-bold">City:</span> Riyadh
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="bg-white border-b-2 border-black px-2 py-0.5 font-black uppercase">
                          Receiver Info
                        </div>
                        <div className="p-2 leading-tight">
                          <p>
                            <span className="font-bold">Name:</span>{" "}
                            {picklist.assignee?.details?.fullName ||
                              "Staff Pending"}
                          </p>
                          <p>
                            <span className="font-bold">Contact:</span>{" "}
                            {picklist.assignee?.email || ""}
                          </p>
                          <p>
                            <span className="font-bold">Address:</span>{" "}
                            {picklist.warehouseId || "Warehouse floor"}
                          </p>
                          <p>
                            <span className="font-bold">City:</span> Riyadh
                          </p>
                          <p>
                            <span className="font-bold">Country:</span> Saudi
                            Arabia
                          </p>
                        </div>
                      </div>
                    </div>
 
                    <div className="border-b-2 border-black py-6 flex flex-col items-center justify-center">
                      <div className="barcode text-6xl tracking-widest">
                        {picklist.picklistNumber}
                      </div>
                      <div className="text-sm font-black mt-1 tracking-[0.2em]">
                        {picklist.picklistNumber}
                      </div>
                    </div>
 
                    <div className="border-b-2 border-black py-1 px-2 text-center font-black uppercase text-[10px]">
                      COD Amount: 0
                    </div>
 
                    <div className="border-b-2 border-black py-6 flex flex-col items-center justify-center">
                      <div className="barcode text-5xl opacity-80">
                        {picklist.id.slice(0, 8).toUpperCase()}
                      </div>
                      <div className="text-xs font-bold mt-1 tracking-widest">
                        {picklist.id.slice(0, 8).toUpperCase()}
                      </div>
                    </div>
 
                    <table className="w-full text-center text-[10px] border-collapse">
                      <thead>
                        <tr className="border-b-2 border-black bg-gray-50">
                          <th className="border-r-2 border-black py-1 px-2 font-black uppercase w-24">
                            SKU
                          </th>
                          <th className="border-r-2 border-black py-1 px-2 font-black uppercase w-20">
                            Item Icon
                          </th>
                          <th className="border-r-2 border-black py-1 px-2 font-black uppercase w-20">
                            Shelve#
                          </th>
                          <th className="border-r-2 border-black py-1 px-2 font-black uppercase">
                            SKU Name
                          </th>
                          <th className="py-1 px-2 font-black uppercase w-12">
                            Qty
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y-2 divide-black">
                        {printItems.map((item: any, idx: number) => (
                          <tr key={idx}>
                            <td className="border-r-2 border-black py-2 px-1 font-bold">
                              {item.sku}
                            </td>
                            <td className="border-r-2 border-black py-2 px-1">
                              <div className="w-10 h-10 border border-gray-200 mx-auto overflow-hidden bg-gray-50 flex items-center justify-center">
                                {item.icon ? (
                                  <img
                                    src={item.icon}
                                    alt={item.sku}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="grid grid-cols-2 gap-0.5 p-1 opacity-20">
                                    <div className="w-2 h-2 bg-black rounded-full"></div>
                                    <div className="w-2 h-2 bg-black rounded-full"></div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="border-r-2 border-black py-2 px-1 font-black text-blue-600">
                              <div className="flex flex-col items-center">
                                <span>{item.location}</span>
                                {item.locationBarcode && (
                                  <div className="barcode-sm scale-75 -mt-2">
                                    {item.locationBarcode}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="border-r-2 border-black py-2 px-2 text-left">
                              {item.name}
                            </td>
                            <td className="py-2 px-1 font-black">{item.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <span>No details available to print</span>
                </div>
              )}
            </div>
 
            <div className="px-10 py-10 flex justify-end">
              <button
                disabled={isLoading || !picklist}
                className="bg-[#FCB910] hover:bg-[#e6a800] disabled:bg-gray-200 text-[#212121] font-black text-lg py-4 px-16 rounded-[24px] shadow-xl shadow-[#FCB910]/20 transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3"
                onClick={handlePrint}
              >
                <IconPrinter size={24} />
                Print Now
              </button>
            </div>
          </div>
        </div>
      </div> */}

      <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
        <link
          href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128&display=swap"
          rel="stylesheet"
        />
        <style>{`
        .barcode {
          font-family: 'Libre Barcode 128', cursive;
          font-size: 40px;
          line-height: 1;
        }
        .barcode-sm {
          font-family: 'Libre Barcode 128', cursive;
          font-size: 32px;
          line-height: 1;
        }
      `}</style>
        <div className="rounded-2xl  xl:w-7/12 lg:w-7/12 w-11/12  relative">
          <div className="flex items-center">
            <div className=" bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px] ">
              <h1 className="text-2xl">
                {t("Print")} ({picklist?.picklistNumber || "..."})
              </h1>
            </div>
            <div className="relative ">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={38}
                height={37}
                viewBox="0 0 38 37"
                fill="none"
                className="absolute -bottom-[13px] -left-[8px] rotate-[-0.1deg]"
              >
                <path
                  d="M0.5 0L4.86189 10.5496C10.4737 24.1222 22.9306 33.645 37.5 35.5L4.91509 37L0.5 0Z"
                  fill="#F7F6F2"
                />
              </svg>

              <button
                onClick={onClose}
                className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>
          <div className="w-full bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
            <div className="overflow-y-auto max-h-[calc(96vh-150px)] px-3 sm:px-6 py-4 sm:py-2">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-500">
                  {t("Select Print Type")}
                </label>
                <div className="relative">
                  <select className="w-full bg-white border border-gray-100 rounded-2xl px-6 py-4 text-xs font-bold text-gray-400 appearance-none focus:outline-none shadow-sm focus:ring-2 focus:ring-[#FCB910]/20 transition-all">
                    <option>
                      {t("Print Pickup List / Print List Seller / Print Pickup List A4 / Print AWB 4*6 / Print AWB A4")}
                    </option>
                  </select>
                  <IconChevronDown
                    size={18}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>

              <div className=" py-2 flex-1 overflow-auto hide-scrollbar">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
                    <IconLoader2
                      size={48}
                      className="animate-spin text-[#FCB910] opacity-50"
                    />
                    <span className="font-bold text-sm tracking-widest uppercase">
                      {t("fetching details")}
                    </span>
                  </div>
                ) : picklist ? (
                  <div className="bg-white rounded-3xl p-8 shadow-inner min-h-full border border-gray-50 relative group">
                    <div
                      ref={printRef}
                      className="w-full max-w-[800px] mx-auto bg-white text-black font-sans border-2 border-black"
                    >
                      {/* Top Header Section */}
                      <div className="grid grid-cols-[1fr_2fr] border-b-2 border-black">
                        <div className="p-4 border-r-2 border-black flex items-center justify-center">
                          <div className="w-12 h-12 border border-gray-300 flex items-center justify-center text-gray-300">
                            <IconX size={20} />
                          </div>
                        </div>
                        <div className="p-4 relative">
                          <div className="absolute top-2 right-4 text-right">
                            <div className="w-16 h-16 border-2 border-black p-1">
                              {/* QR Code Placeholder */}
                              <div className="grid grid-cols-4 gap-0.5 h-full opacity-80">
                                {Array.from({ length: 16 }).map((_, i) => (
                                  <div
                                    key={i}
                                    className={`bg-black ${Math.random() > 0.5 ? "opacity-100" : "opacity-0"}`}
                                  ></div>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="mt-8 text-[10px] font-bold">
                            Order Date: {formatDate(picklist.createdAt)}
                          </div>
                        </div>
                      </div>

                      {/* Sender & Receiver Info */}
                      <div className="grid grid-cols-2 border-b-2 border-black text-[10px]">
                        <div className="border-r-2 border-black">
                          <div className="bg-white border-b-2 border-black px-2 py-0.5 font-black uppercase">
                            {t("sender info")}
                          </div>
                          <div className="p-2 leading-tight">
                            <p>
                              <span className="font-bold">{t("name")}:</span>{" "}
                              {t(picklist.client?.name || "Warehouse Manager")}
                            </p>
                            <p>
                              <span className="font-bold">{t("contact")}:</span>{" "}
                              {picklist.client?.email || ""}
                            </p>
                            <p>
                              <span className="font-bold">{t("address")}:</span> {t("Main Hub")}
                            </p>
                            <p>
                              <span className="font-bold">{t("city")}:</span> {t("Riyadh")}
                            </p>
                          </div>
                        </div>
                        <div>
                          <div className="bg-white border-b-2 border-black px-2 py-0.5 font-black uppercase">
                            {t("receiver info")}
                          </div>
                          <div className="p-2 leading-tight">
                            <p>
                              <span className="font-bold">{t("name")}:</span>{" "}
                              {t(picklist.assignee?.details?.fullName || "Staff Pending")}
                            </p>
                            <p>
                              <span className="font-bold">{t("contact")}:</span>{" "}
                              {picklist.assignee?.email || ""}
                            </p>
                            <p>
                              <span className="font-bold">{t("address")}:</span>{" "}
                              {t(picklist.warehouseId || "Warehouse floor")}
                            </p>
                            <p>
                              <span className="font-bold">{t("city")}:</span> {t("Riyadh")}
                            </p>
                            <p>
                              <span className="font-bold">{t("country")}:</span> {t("Saudi Arabia")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Main Tracking Barcode */}
                      <div className="border-b-2 border-black py-6 flex flex-col items-center justify-center">
                        <div className="barcode text-6xl tracking-widest">
                          {picklist.picklistNumber}
                        </div>
                        <div className="text-sm font-black mt-1 tracking-[0.2em]">
                          {picklist.picklistNumber}
                        </div>
                      </div>

                      {/* COD Amount Section */}
                      <div className="border-b-2 border-black py-1 px-2 text-center font-black uppercase text-[10px]">
                        {t("cod amount: 0")}
                      </div>

                      {/* Secondary Barcode */}
                      <div className="border-b-2 border-black py-6 flex flex-col items-center justify-center">
                        <div className="barcode text-5xl opacity-80">
                          {picklist.id.slice(0, 8).toUpperCase()}
                        </div>
                        <div className="text-xs font-bold mt-1 tracking-widest">
                          {picklist.id.slice(0, 8).toUpperCase()}
                        </div>
                      </div>

                      {/* Items Table */}
                      <table className="w-full text-center text-[10px] border-collapse">
                        <thead>
                          <tr className="border-b-2 border-black bg-gray-50">
                            <th className="border-r-2 border-black py-1 px-2 font-black uppercase w-24">
                              {t("sku")}
                            </th>
                            <th className="border-r-2 border-black py-1 px-2 font-black uppercase w-20">
                              {t("item icon")}
                            </th>
                            <th className="border-r-2 border-black py-1 px-2 font-black uppercase w-20">
                              {t("shelve#")}
                            </th>
                            <th className="border-r-2 border-black py-1 px-2 font-black uppercase">
                              {t("sku name")}
                            </th>
                            <th className="py-1 px-2 font-black uppercase w-12">
                              {t("qty")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y-2 divide-black">
                          {printItems.map((item: any, idx: number) => (
                            <tr key={idx}>
                              <td className="border-r-2 border-black py-2 px-1 font-bold">
                                {item.sku}
                              </td>
                              <td className="border-r-2 border-black py-2 px-1">
                                <div className="w-10 h-10 border border-gray-200 mx-auto overflow-hidden bg-gray-50 flex items-center justify-center">
                                  {item.icon ? (
                                    <img
                                      src={item.icon}
                                      alt={item.sku}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="grid grid-cols-2 gap-0.5 p-1 opacity-20">
                                      <div className="w-2 h-2 bg-black rounded-full"></div>
                                      <div className="w-2 h-2 bg-black rounded-full"></div>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="border-r-2 border-black py-2 px-1 font-black text-blue-600">
                                <div className="flex flex-col items-center">
                                  <span>{item.location}</span>
                                  {item.locationBarcode && (
                                    <div className="barcode-sm scale-75 -mt-2">
                                      {item.locationBarcode}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="border-r-2 border-black py-2 px-2 text-left">
                                {item.name}
                              </td>
                              <td className="py-2 px-1 font-black">
                                {item.qty}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <span>{t("no details available to print")}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2  bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px] ">
            <button
              className="w-full sm:w-auto h-11 px-6 rounded-full border border-[#D8D8D8] bg-white hover:bg-[#F5F5F5] transition-all duration-200 font-medium cursor-pointer"
              onClick={onClose}
            >
              {t("cancel")}
            </button>

            <button
              onClick={handlePrint}
              disabled={isLoading || !picklist}
              className={`${
                isLoading || picklist === null
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#FBD037] hover:bg-yellow-400 text-[#212121] shadow-md active:scale-95"
              } flex items-center rounded-full px-8 py-2 space-x-2 transition-all text-base font-medium`}
            >
              {isLoading && (
                <svg
                  className="animate-spin h-5 w-5 text-[#212121]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
                  />
                </svg>
              )}
              <span>{isLoading ? t("processing...") : t("print")}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintModal;
